







































































































































import { Component, Emit, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { validateEmailAddresses } from "@/utils/EmailUtils";
import { MemberClientDetails, MIN_PASSWORD_LENGTH, SyncProjectsWith } from "@/data/models/Client";
import {
  createNewMemberClient,
  doesOtherClientWithEmailExist, 
  doesOtherClientWithIresProjectNumberPrefixExist, 
  updateMemberClientDetails 
} from "@/data/repository/ClientRepository";
import { compareArraysIgnoreOrder, filterOutUndefined } from "@/utils/Utils";
import { ExternalSystem } from "@/data/models/ExternalSystem";
import MultiSelectionPickerComponent from "../common/MultiSelectionPicker.vue";

interface InitialMemberClientDetails {
  clientName: string | null;
  iresProjectNumberPrefix: string | null;
  email: string | null;
  zip: string | null;
  city: string | null;
  street: string | null;
  houseNumber: string | null;
  syncProjectsWith: SyncProjectsWith | null;
}

@Component({
  methods: {},
  components: { 
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "multi-selection-picker": MultiSelectionPickerComponent,
  },
})

export default class MemberClientDialog extends Vue {
  @Ref("baseDialog")
  baseDialog: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  memberClientDetails: MemberClientDetails | null = null;
  externalSystems: ExternalSystem[] = [];

  selectedExternalSystems: ExternalSystem[] = [];
  externalSystemIds: string[] = [];

  clientName: string | null = null;
  iresProjectNumberPrefix: string | null = null;
  email: string | null = null;
  password: string | null = null;

  zip: string | null = null;
  city: string | null = null;
  street: string | null = null;
  houseNumber: string | null = null;

  showPassword = false;

  isCreateClientDialog = false;
  isEditing = false;
  isLoading = false;

  title = "";

  iresProjectNumberPrefixErrors: string[] = [];
  clientNameErrors: string[] = [];
  emailErrors: string[] = [];
  passwordErrors: string[] = [];

  initialMemberClientDetails: InitialMemberClientDetails = this.createDefaultMemberClientDetails();

  @Emit("onSuccess")
  onSuccess(message: string, memberClientId?: string) {
    this.$emit("onSuccess", message, memberClientId);
    this.stopLoading();
    this.baseDialog.isShowing = false;
  }

  @Emit("onError")
  onError(message: string) {
    this.$emit("onError", message);
    this.stopLoading();
  }

  @Watch("hasDetailsEdited")
  onDetailsChanged() {
    this.baseDialog.negativeButtonText = this.calculateNegativeButtonText();
  }

  get selectedExternalSystemIds() {
    return this.selectedExternalSystems?.map((ext: ExternalSystem) => ext.id);
  }

  get hasDetailsEdited() {
    return this.clientName !== this.initialMemberClientDetails.clientName || 
      this.iresProjectNumberPrefix !== this.initialMemberClientDetails.iresProjectNumberPrefix ||
      this.email !== this.initialMemberClientDetails.email ||
      (!this.isCreateClientDialog && this.password !== null) ||
      this.zip !== this.initialMemberClientDetails.zip || 
      this.city !== this.initialMemberClientDetails.city ||
      this.street !== this.initialMemberClientDetails.street ||
      this.houseNumber !== this.initialMemberClientDetails.houseNumber ||
      !compareArraysIgnoreOrder(this.externalSystemIds, this.selectedExternalSystemIds);
  }

  get readonly() {
    return !this.isEditing;
  }

  get shouldDisableSave() {
    return this.clientName === null || 
      this.iresProjectNumberPrefix === null ||
      this.email === null ||
      (!this.isCreateClientDialog && this.password !== null);
  }

  get showExternalSystemSelection() {
    return this.externalSystems.length > 0;
  }

  externalSystemsFormatter(externalSystem: ExternalSystem) {
    return externalSystem.name;
  }

  calculatePositiveButtonText() {
    return this.$t(`common.${this.isEditing
      ? 'save'
      : 'edit'
    }`).toString();
  }

  calculateNegativeButtonText() {
    if (this.isCreateClientDialog) {
      return this.$t(`common.${this.hasDetailsEdited
        ? 'abort'
        : 'close'
      }`).toString(); 
    }

    return this.$t(`common.${this.isEditing && this.hasDetailsEdited
      ? 'abort'
      : 'close'
    }`).toString(); 
  }

  createDefaultMemberClientDetails() {
    return {
      clientName: null,
      iresProjectNumberPrefix: null,
      email: null,
      zip: null,
      city: null,
      street: null,
      houseNumber: null,
    } as InitialMemberClientDetails;
  }

  initializeMemberClientDetails(
    memberClient: MemberClientDetails | null
  ) {
    this.externalSystemIds = memberClient?.syncProjectsWith?.erpSystems ?? [];
    this.clientName = memberClient?.clientName ?? null;
    this.iresProjectNumberPrefix = memberClient?.iresProjectNumberPrefix ?? null;
    this.email = memberClient?.email ?? null;

    this.zip = memberClient?.zip ?? null;
    this.city = memberClient?.city ?? null;
    this.street = memberClient?.street ?? null;
    this.houseNumber = memberClient?.houseNumber ?? null;

    this.initialMemberClientDetails = {
      clientName: this.clientName,
      iresProjectNumberPrefix: this.iresProjectNumberPrefix,
      email: this.email,
      zip: this.zip,
      city: this.city,
      street: this.street,
      houseNumber: this.houseNumber,
      syncProjectsWith: memberClient?.syncProjectsWith ?? null,
    };

    this.selectedExternalSystems = this.externalSystems.filter(ext => this.externalSystemIds.includes(ext.id)) ?? null; 
  }

  lowercase() {
    if (!this.email) {
      return;
    }

    this.email = this.email.toLowerCase();
  }

  onPositiveButtonClick() {
    if (!this.isEditing) {
      this.onStartEdit();
    } else if (this.isEditing) {
      this.onSubmit();
    }
  }

  onStartEdit() {
    this.resetErrors();
    this.isEditing = true;

    this.baseDialog.positiveButtonText = this.calculatePositiveButtonText();
    this.baseDialog.negativeButtonText = this.calculateNegativeButtonText();
  }

  resetDialog() {
    this.baseDialog.isShowing = false;
    this.isEditing = false;
    this.isCreateClientDialog = false;
    
    this.password = null
    this.initializeMemberClientDetails(null);
  }

  resetErrors() {
    this.iresProjectNumberPrefixErrors = [];
    this.clientNameErrors = [];
    this.emailErrors = [];
    this.passwordErrors = [];
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  async validateInput(clientId: string) {
    const errorsStringResources = "settings.administrator.memberClientDialog.errors.";

    if (!this.iresProjectNumberPrefix || this.iresProjectNumberPrefix === "") {
      this.iresProjectNumberPrefixErrors.push(this.$t(`${errorsStringResources}emptyIresProjectNumberPrefix`).toString());
      return false;
    }

    // The Kürzel rule of the IRES Admin Client (ticket 2610048): four characters in capitals (typing
    // already uppercases; a pasted value skips that), unique ignoring case. Only a changed Kürzel is
    // checked, so an older stored one ("Svt ") never blocks other edits of the Niederlassung.
    if (this.iresProjectNumberPrefix !== this.initialMemberClientDetails.iresProjectNumberPrefix) {
      this.iresProjectNumberPrefix = this.iresProjectNumberPrefix.trim().toUpperCase();

      if (this.iresProjectNumberPrefix.length !== 4) {
        this.iresProjectNumberPrefixErrors.push(this.$t(`${errorsStringResources}invalidIresProjectNumberPrefix`).toString());
        return false;
      }

      const isDuplicateIresProjectNumberPrefix = await doesOtherClientWithIresProjectNumberPrefixExist(clientId, this.iresProjectNumberPrefix);
      if (isDuplicateIresProjectNumberPrefix) {
        this.iresProjectNumberPrefixErrors.push(this.$t(`${errorsStringResources}duplicateIresProjectNumberPrefix`).toString());
        return false;
      }
    }

    if (!this.clientName || this.clientName === "") {
      this.clientNameErrors.push(this.$t(`${errorsStringResources}emptyClientName`).toString());
      return false;
    }

    if (!this.email || this.email === "") {
      this.emailErrors.push(this.$t(`${errorsStringResources}emptyEmail`).toString());
      return false;
    }

    if (!validateEmailAddresses(this.email)) {
      this.emailErrors.push(this.$t(`${errorsStringResources}invalidEmail`).toString());
      return false;
    }

    const isDuplicateEmail = await doesOtherClientWithEmailExist(clientId, this.email);
    if (isDuplicateEmail) {
      this.emailErrors.push(this.$t(`${errorsStringResources}duplicateEmail`).toString());
      return false;
    }

    if (this.isCreateClientDialog) {
      if (!this.password || this.password === "") {
        this.passwordErrors.push(this.$t(`${errorsStringResources}emptyPassword`).toString());
        return false;
      }

      if (this.password.length < MIN_PASSWORD_LENGTH) {
      this.passwordErrors.push(this.$t(`${errorsStringResources}invalidPassword`, [MIN_PASSWORD_LENGTH]).toString());
      return false;
    }
    }

    return true;
  }

  startLoading() {
    this.isLoading = true;
  }

  stopLoading() {
    this.isLoading = false;
  }

  uppercase() {
    if (!this.iresProjectNumberPrefix) {
      return;
    }

    this.iresProjectNumberPrefix = this.iresProjectNumberPrefix.toUpperCase();
  }

  async show(
    memberClientDetails?: MemberClientDetails,
    externalSystems: ExternalSystem[] = [],
  ) {
    this.isCreateClientDialog = !memberClientDetails;
    this.isEditing = this.isCreateClientDialog;
      
    this.title = this.$t(`settings.administrator.memberClientDialog.title${this.isCreateClientDialog 
      ? 'Create' 
      : 'Edit'
    }`).toString();

    this.externalSystems = externalSystems;
    if (memberClientDetails) {
      this.initializeMemberClientDetails(memberClientDetails);
      this.memberClientDetails = memberClientDetails;
    }
    
    this.baseDialog.show({
      title: this.title,
      positiveButtonText: this.calculatePositiveButtonText(),
      negativeButtonText: this.calculateNegativeButtonText(),
      closeOnSave: false,
      maxWidth: 800
    });

    this.resetErrors();
  }

  async onSubmit() {
    this.startLoading();
    const clientId = this.memberClientDetails?.id ?? "";

    // Validate the input
    const isInputValid = await this.validateInput(clientId); 
    if (!isInputValid) {
      this.onError("common.changesNotSaved");
      this.stopLoading();
      return;
    }

    const syncProjectsWith: SyncProjectsWith | null = this.selectedExternalSystemIds.length > 0
    ?  {
        erpSystems: this.selectedExternalSystemIds,
        insurancePlatforms: []
      }
    : null;

    const memberClientDetails = filterOutUndefined({
      clientName: this.clientName ?? "",
      iresProjectNumberPrefix: this.iresProjectNumberPrefix ?? "",
      email: this.isCreateClientDialog ? this.email ?? "" : undefined,
      zip: this.zip ?? "",
      city: this.city ?? "",
      street: this.street ?? "",
      houseNumber: this.houseNumber ?? "",
      syncProjectsWith: syncProjectsWith,
    })

    if (this.isCreateClientDialog) {
      try {
        const result = await createNewMemberClient(memberClientDetails, this.password ?? "");

        if (!result) {
          this.onError("settings.administrator.memberClientDialog.createClientErrorMessage");
          this.stopLoading();
          return;
        }

        // On success
        this.onSuccess("settings.administrator.memberClientDialog.createClientSuccessMessage", result.id);
      } catch (error) {
        this.onError("settings.administrator.memberClientDialog.createClientErrorMessage");
        this.stopLoading();
        return;
      }
    } else {
      try {
        // Update existing member client details
        const result = await updateMemberClientDetails(clientId, memberClientDetails);

        if (!result) {
          this.onError("settings.administrator.memberClientDialog.editClientErrorMessage");
          return;
        }

        this.onSuccess("settings.administrator.memberClientDialog.editClientSuccessMessage");
      } catch (error) {
        this.onError("settings.administrator.memberClientDialog.editClientErrorMessage");
        this.stopLoading();
        return;
      }
    }

    // Reset the dialog
    this.resetDialog();
    this.stopLoading();
  }

  async onAbortCreateClient() {
    if (this.isEditing) {
      if (this.hasDetailsEdited) {
        const result = await this.confirmationDialogComponent.show({
          title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
          message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
          rejectMessage: this.$t("common.back").toString(),
          acceptMessage: this.$t("common.abort").toString(),
        });

        if (result) {
          this.resetDialog();
        }
      }
      else {
        this.resetDialog();
      }
    }
  }
}
